import { DateTime } from "luxon";
import { formatMonthDayWithYearIfNeeded } from "app/utils/luxonFormats";

// CN-2832: "Today" read as the day the coach set it, not the day someone buys.
export const bill_start_options = [{ value: "today", label: "Day of purchase"},
{ value: "first_of_month", label: "1st of Month" },
{ value: "fifteenth_of_month", label: "15th of Month" },
{ value: "custom_date", label: "Custom date" }];

export const currency_symbol = { 'USD': '$',
    'AUD': '$' ,
    'GBP': '£',
    'CAD': '$',
    'EUR': '€',
    'JPY': '¥',
    'AED': 'AED',
    'MXN': 'MXN',
    'NZD': '$',
    'TRY': '₺',
    'CNY': '¥',
    'ZAR': 'R',
    'CHF': 'SFr.',
    'NOK': 'kr',
    'CZK': 'Kč',
    'HKD': 'HK$',
    'THB': '฿',
};

export const currency = [{ value: 'USD', label: 'US Dollar (USD, $)' },
    { value: 'AUD', label: 'Australian Dollar (AUD, $)' },
    { value: 'GBP', label: 'British Pound (GBP, £)'},
    { value: 'CAD', label: 'Canadian Dollar (CAD, $)' },
    { value: 'EUR', label: 'Euro (EUR, €)', currency: '€' },
    { value: 'JPY', label: 'Japanese Yen (JPY, ¥)'},
    { value: 'AED', label: 'United Arab Emirates Dirham (AED)' },
    { value: 'MXN', label: 'Mexican Peso (MXN)' },
    { value: 'NZD', label: 'New Zealand Dollar (NZD, $)' },
    { value: 'TRY', label: 'Turkish Lira (TRY, ₺)' },
    { value: 'CNY', label: 'Chinese Yuan (CNY, ¥)' },
    { value: 'ZAR', label: 'South African Rand (ZAR, R)' },
    { value: 'CHF', label: 'Swiss Franc (CHF, SFr.)' },
    { value: 'NOK', label: 'Norwegian Krone (NOK, kr)'},
    { value: 'CZK', label: 'Czech Koruna (CZK, Kč)'},
    { value: 'HKD', label: 'Hong Kong Dollar (HKD, HK$)' },
    { value: 'THB', label: 'Thai Baht (THB, ฿)' }
  ];

// Pricing unit options for subscription billing intervals
// Values are integers: 0 = months, 1 = hours (matches API response)
export const pricing_unit_options = [
  { value: 0, label: 'Month(s)' },
  // { value: 1, label: 'Hour(s)' },
];

// --- Package relative expiry (CN-2641) ---
// The API stores expires_in_days (days only); the UI offers friendlier units
// and converts on submit (1 month = 30 days).

export const expiry_unit_options = [
  { value: "days", label: "Day(s)" },
  { value: "weeks", label: "Week(s)" },
  { value: "months", label: "Month(s)" },
];

export const daysPerExpiryUnit: Record<string, number> = { days: 1, weeks: 7, months: 30 };

/**
 * Matches StripeProductParamsContract::MAX_EXPIRES_IN_DAYS — a longer-lived
 * package should simply never expire.
 */
export const MAX_EXPIRES_IN_DAYS = 365;

/** Matches StripeProductParamsContract::MAX_BILLING_CYCLES. */
export const MAX_BILLING_CYCLES = 120;

/**
 * Render a stored day count back in the largest unit that divides it evenly, so
 * the fewest whole units win: 60 days reads "2 Month(s)", 28 reads "4 Week(s)".
 *
 * The unit itself is not stored, so a value divisible by both 7 and 30 can come
 * back in the other unit — with expires_in_days capped at 365 the only such
 * value is 210, which reads "7 Month(s)" even if "30 Week(s)" was typed. That is
 * the smaller count, so it is the one we want.
 */
export function decomposeExpiry(expiresInDays: number | null | undefined): {
  expires_in_value: number | null;
  expires_in_unit: { value: string; label: string };
} {
  if (!expiresInDays) {
    return { expires_in_value: null, expires_in_unit: expiry_unit_options[0] };
  }
  if (expiresInDays % 30 === 0) {
    return { expires_in_value: expiresInDays / 30, expires_in_unit: expiry_unit_options[2] };
  }
  if (expiresInDays % 7 === 0) {
    return { expires_in_value: expiresInDays / 7, expires_in_unit: expiry_unit_options[1] };
  }
  return { expires_in_value: expiresInDays, expires_in_unit: expiry_unit_options[0] };
}

// Singular/plural picked in code rather than via i18next's `count`: this repo's
// `_plural` entries are placeholders ("Day_plural"), so relying on them would
// print the key. Interpolating as `amount` also keeps i18next from switching to
// plural lookup on these sentences.
const EXPIRY_DURATION_LABELS: Record<string, { one: string; other: string }> = {
  days: { one: "1 day", other: "{{amount}} days" },
  weeks: { one: "1 week", other: "{{amount}} weeks" },
  months: { one: "1 month", other: "{{amount}} months" },
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * Shared react-hook-form `validate` rule for the package expiry value: a whole
 * number of at least 1, and at most one year once the selected unit is applied
 * (the API rejects anything longer — StripeProductParamsContract).
 */
export const expiryValueRule = (t: Translate) => ({
  isValid: (
    value: unknown,
    formValues?: { expires_in_unit?: { value: string } },
  ): boolean | string => {
    if (!value) {
      return true;
    }
    const amount = Number(value);
    if (!Number.isInteger(amount) || amount < 1) {
      return t("The value must be a whole number of at least 1");
    }
    const unit = formValues?.expires_in_unit?.value ?? "days";
    return (
      amount * daysPerExpiryUnit[unit] <= MAX_EXPIRES_IN_DAYS ||
      t("Credits can expire at most 1 year after they are granted")
    );
  },
});

/** Shared `validate` rule for the subscription cycle cap. */
export const billingCyclesRule = (t: Translate) => ({
  isValid: (value: unknown): boolean | string => {
    if (!value) {
      return true;
    }
    const amount = Number(value);
    if (!Number.isInteger(amount) || amount < 1) {
      return t("The value must be a whole number of at least 1");
    }
    return (
      amount <= MAX_BILLING_CYCLES ||
      t("The limit must be at most {{max}} billing cycles", { max: MAX_BILLING_CYCLES })
    );
  },
});

/**
 * "60 days" stored as days, shown to the athlete in the unit the coach picked —
 * "2 months" — so the storefront and the offering form agree.
 */
export function humanizeExpiry(
  expiresInDays: number | null | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const { expires_in_value: amount, expires_in_unit: unit } = decomposeExpiry(expiresInDays);
  if (!amount) {
    return "";
  }
  const label = EXPIRY_DURATION_LABELS[unit.value];
  return amount === 1 ? t(label.one) : t(label.other, { amount });
}

/**
 * CFTB-3418 / CFTB-3419: the one sentence describing an offering's configured
 * limit — expiry for packages, cycle cap for subscriptions. Null when the
 * offering has neither.
 *
 * Shared because three surfaces show it (coach offering card, athlete
 * storefront card, offering details modal) and they must not drift apart in
 * wording. Each caller styles its own element; only the copy is shared.
 */
export function offeringLimitNote(
  product: {
    productType?: string;
    expiresInDays?: number | null;
    maxBillingCycles?: number | null;
  },
  t: Translate,
): string | null {
  if (product.productType === "product" && product.expiresInDays != null) {
    return t("Credits valid for {{duration}} after purchase", {
      duration: humanizeExpiry(product.expiresInDays, t),
    });
  }
  if (product.productType === "subscription" && product.maxBillingCycles != null) {
    // Same wording as the offering form's field, so the card reads back what
    // the coach typed there.
    return product.maxBillingCycles === 1
      ? t("Auto Renewal Ends After 1 Billing Cycle")
      : t("Auto Renewal Ends After {{amount}} Billing Cycles", { amount: product.maxBillingCycles });
  }
  return null;
}

// --- Weekday hour restriction utilities ---

export const ALL_DAY_BEGIN = 0;
export const ALL_DAY_END = 1440;

/** Default interval (minutes) when facility has no scheduling_interval set. */
export const DEFAULT_SCHEDULING_INTERVAL = 15;

/**
 * Generate interval-based options for the "From" hour dropdown.
 * Returns options from 12:00 AM (0) through the last interval before midnight.
 */
export function generateFromHourOptions(
  interval: number = DEFAULT_SCHEDULING_INTERVAL,
): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];

  for (let totalMinutes = 0; totalMinutes < 1440; totalMinutes += interval) {
    const label = formatMinutesToTime(totalMinutes);
    options.push({ value: String(totalMinutes), label });
  }

  return options;
}

/**
 * Generate interval-based options for the "To" hour dropdown.
 * Returns the same time slots as the "From" dropdown: 12:00 AM (0) through
 * the last interval before midnight. No special end-of-day / next-day entry.
 */
export function generateToHourOptions(
  interval: number = DEFAULT_SCHEDULING_INTERVAL,
): { value: string; label: string }[] {
  return generateFromHourOptions(interval);
}

/**
 * Convert minutes from midnight to a 12-hour time string.
 * e.g. 0 -> "12:00 AM", 540 -> "9:00 AM", 900 -> "3:00 PM", 1440 -> "12:00 AM"
 */
export function formatMinutesToTime(minutes: number): string {
  const normalized = minutes % 1440;
  const hours = Math.floor(normalized / 60);
  const mins = normalized % 60;
  const period = hours < 12 ? "AM" : "PM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${mins.toString().padStart(2, "0")} ${period}`;
}

/**
 * Build display label for weekday restriction including hour range.
 * Returns "Weekdays" for full-day, or "Weekdays 9:00 AM - 3:00 PM" for specific hours.
 */
export function weekdayRestrictionLabel(
  beginAt: number,
  endAt: number,
  weekdaysText: string = "Weekdays",
): string {
  if (beginAt === ALL_DAY_BEGIN && endAt === ALL_DAY_END) {
    return weekdaysText;
  }
  return `${weekdaysText} ${formatMinutesToTime(beginAt)} - ${formatMinutesToTime(endAt)}`;
}

/**
 * Check if begin/end represent full-day (All Day).
 */
export function isAllDay(beginAt: number, endAt: number): boolean {
  return beginAt === ALL_DAY_BEGIN && endAt === ALL_DAY_END;
}

// CN-2832: the athlete-facing promise for a subscription with a custom start
// date that is still ahead: "You won't be charged today. Your first payment of
// $100 is on Nov 1, 2026, then $100 on the 1st of each month for 6 months."
// Null for every other subscription and once the date has passed.
export const scheduledFirstChargeText = (
  product: {
    billStartDate?: string | null;
    billingStartOn?: string | null;
    recurring?: string | number | null;
    maxBillingCycles?: number | null;
  },
  price: string,
  t: (key: string, options?: Record<string, unknown>) => string,
  formatDate: (date: Date) => string,
): string | null => {
  if (product.billStartDate !== "custom_date" || !product.billingStartOn) {
    return null;
  }
  const [year, month, day] = product.billingStartOn.split("-").map(Number);
  const startsOn = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (startsOn <= today) {
    return null;
  }

  const months = Number(product.recurring) || 1;
  const cadence = months === 1
    // dayNumber lets languages without English ordinals say "5日".
    ? t("on the {{day}} of each month", { day: ordinal(day), dayNumber: day })
    : t("every {{count}} months", { count: months });
  const total = product.maxBillingCycles ? product.maxBillingCycles * months : null;

  return t(
    "You won't be charged today. Your first payment of {{price}} is on {{date}}, then {{price}} {{cadence}}{{duration}}.",
    {
      price,
      date: formatDate(startsOn),
      cadence,
      duration: total ? ` ${t("for {{count}} months", { count: total })}` : "",
    },
  );
};

// The same text with the app's date format, for every place that shows an
// offering to the athlete: the store card, the coach page card, the public
// profile card and the details modal.
export const scheduledFirstChargeNote = (
  product: Parameters<typeof scheduledFirstChargeText>[0],
  price: string,
  t: Parameters<typeof scheduledFirstChargeText>[2],
): string | null =>
  scheduledFirstChargeText(product, price, t, (date) =>
    formatMonthDayWithYearIfNeeded(DateTime.fromJSDate(date)),
  );

const ordinal = (day: number): string => {
  const suffixes: Record<number, string> = { 1: "st", 2: "nd", 3: "rd" };
  const teen = day % 100 >= 11 && day % 100 <= 13;
  return `${day}${teen ? "th" : suffixes[day % 10] || "th"}`;
};
